import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Activity, TrendingUp, Clock, AlertTriangle, CheckCircle2, XCircle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

interface QueueMetrics {
  pending: number;
  processing: number;
  completed: number;
  failed: number;
  avgProcessingTime: number;
  successRate: number;
  throughput: number;
}

interface TimeSeriesData {
  time: string;
  completed: number;
  failed: number;
  avgTime: number;
}

export function PerformanceMonitor() {
  const [timeRange, setTimeRange] = useState<'1h' | '24h' | 'all'>('1h');
  const [metrics, setMetrics] = useState<QueueMetrics>({
    pending: 0,
    processing: 0,
    completed: 0,
    failed: 0,
    avgProcessingTime: 0,
    successRate: 0,
    throughput: 0,
  });
  
  const [timeSeriesData, setTimeSeriesData] = useState<TimeSeriesData[]>([]);
  const [isBacklogAlert, setIsBacklogAlert] = useState(false);

  useEffect(() => {
    fetchMetrics();
    
    // Poll metrics every 3 seconds
    const interval = setInterval(fetchMetrics, 3000);
    
    // Subscribe to real-time updates
    const channel = supabase
      .channel('queue-monitoring')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'analysis_job_queue',
        },
        () => {
          fetchMetrics();
        }
      )
      .subscribe();

    return () => {
      clearInterval(interval);
      channel.unsubscribe();
    };
  }, [timeRange]);

  const fetchMetrics = async () => {
    try {
      const now = new Date();
      let timeFilter = null;
      
      if (timeRange === '1h') {
        timeFilter = new Date(now.getTime() - 60 * 60 * 1000).toISOString();
      } else if (timeRange === '24h') {
        timeFilter = new Date(now.getTime() - 24 * 60 * 60 * 1000).toISOString();
      }

      // Get current queue status counts
      let query = supabase
        .from('analysis_job_queue')
        .select('status, processing_time_ms, created_at, completed_at');
      
      if (timeFilter) {
        query = query.gte('created_at', timeFilter);
      }
      
      const { data: statusData } = await query;

      if (!statusData) return;

      const pending = statusData.filter(j => j.status === 'pending').length;
      const processing = statusData.filter(j => j.status === 'processing').length;
      const completed = statusData.filter(j => j.status === 'completed').length;
      const failed = statusData.filter(j => j.status === 'failed').length;

      // Calculate average processing time (completed jobs only)
      const completedJobs = statusData.filter(j => j.status === 'completed' && j.processing_time_ms);
      const avgProcessingTime = completedJobs.length > 0
        ? completedJobs.reduce((sum, j) => sum + (j.processing_time_ms || 0), 0) / completedJobs.length
        : 0;

      // Calculate success rate
      const totalProcessed = completed + failed;
      const successRate = totalProcessed > 0 ? (completed / totalProcessed) * 100 : 0;

      // Calculate throughput (jobs per minute in last 5 minutes)
      const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000).toISOString();
      const recentCompleted = statusData.filter(
        j => j.completed_at && j.completed_at > fiveMinutesAgo
      ).length;
      const throughput = (recentCompleted / 5).toFixed(1);

      // Check for backlog alert (more than 10 pending or 5+ minutes old)
      const oldestPending = statusData
        .filter(j => j.status === 'pending')
        .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())[0];
      
      const backlogAlert = pending > 10 || (
        oldestPending && 
        (Date.now() - new Date(oldestPending.created_at).getTime()) > 5 * 60 * 1000
      );

      setMetrics({
        pending,
        processing,
        completed,
        failed,
        avgProcessingTime,
        successRate,
        throughput: parseFloat(throughput),
      });

      setIsBacklogAlert(backlogAlert);

      // Update time series data (last 10 data points)
      setTimeSeriesData(prev => {
        const newPoint: TimeSeriesData = {
          time: new Date().toLocaleTimeString(),
          completed,
          failed,
          avgTime: Math.round(avgProcessingTime),
        };
        
        const updated = [...prev, newPoint].slice(-10);
        return updated;
      });
    } catch (error) {
      console.error('Failed to fetch metrics:', error);
    }
  };

  return (
    <div className="space-y-4">
      {/* Time Range Selector */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">Time Range</p>
              <p className="text-xs text-muted-foreground">Filter metrics by time period</p>
            </div>
            <Select value={timeRange} onValueChange={(value: '1h' | '24h' | 'all') => setTimeRange(value)}>
              <SelectTrigger className="w-[140px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1h">Last Hour</SelectItem>
                <SelectItem value="24h">Last 24 Hours</SelectItem>
                <SelectItem value="all">All Time</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Alert Banner */}
      {isBacklogAlert && (
        <Card className="border-orange-500 bg-orange-50 dark:bg-orange-950/20">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-orange-700 dark:text-orange-400">
              <AlertTriangle className="w-5 h-5" />
              <p className="font-medium">Queue Backlog Alert: High pending count or old jobs detected</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Real-time Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Pending</p>
                <p className="text-2xl font-bold">{metrics.pending}</p>
              </div>
              <Clock className="w-8 h-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Processing</p>
                <p className="text-2xl font-bold">{metrics.processing}</p>
              </div>
              <Activity className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Completed</p>
                <p className="text-2xl font-bold text-green-600">{metrics.completed}</p>
              </div>
              <CheckCircle2 className="w-8 h-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Failed</p>
                <p className="text-2xl font-bold text-red-600">{metrics.failed}</p>
              </div>
              <XCircle className="w-8 h-8 text-red-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Performance Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">Avg Processing Time</p>
                <TrendingUp className="w-4 h-4 text-muted-foreground" />
              </div>
              <p className="text-2xl font-bold">{metrics.avgProcessingTime.toFixed(0)}ms</p>
              <p className="text-xs text-muted-foreground">Per job completion</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">Success Rate</p>
              <p className="text-2xl font-bold">{metrics.successRate.toFixed(1)}%</p>
              <div className="flex items-center gap-2">
                <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-green-500 transition-all"
                    style={{ width: `${metrics.successRate}%` }}
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">Throughput</p>
              <p className="text-2xl font-bold">{metrics.throughput}</p>
              <p className="text-xs text-muted-foreground">Jobs per minute (5min avg)</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Time Series Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Completion Trend</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={timeSeriesData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="time" fontSize={12} />
                <YAxis fontSize={12} />
                <Tooltip />
                <Line 
                  type="monotone" 
                  dataKey="completed" 
                  stroke="#10b981" 
                  strokeWidth={2}
                  name="Completed"
                />
                <Line 
                  type="monotone" 
                  dataKey="failed" 
                  stroke="#ef4444" 
                  strokeWidth={2}
                  name="Failed"
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Processing Time Trend</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={timeSeriesData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="time" fontSize={12} />
                <YAxis fontSize={12} />
                <Tooltip />
                <Bar 
                  dataKey="avgTime" 
                  fill="#3b82f6"
                  name="Avg Time (ms)"
                />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Status Badge */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">Queue Health Status</p>
              <p className="text-xs text-muted-foreground">Based on backlog and failure rate</p>
            </div>
            <Badge 
              variant={
                isBacklogAlert ? "destructive" : 
                metrics.successRate < 80 ? "secondary" : 
                "default"
              }
              className="text-sm"
            >
              {isBacklogAlert ? "⚠️ Backlog" : 
               metrics.successRate < 80 ? "⚡ Degraded" : 
               "✓ Healthy"}
            </Badge>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
